import type { GuidelineRepresentation } from '@atlassian/data-classification-level';

import type { ClassificationLevel } from '../ClassificationRadioOption.tsx';

type GuidelineProps = {
	guideline: string | undefined;
	guidelineRepresentation: GuidelineRepresentation;
};

/**
 * Returns the appropriate guideline value and representation format based on feature flag.
 *
 * When the 'confluence_dlp_guideline_rich_text_support' feature flag is enabled and ADF guideline data exists,
 * this function returns the ADF content with 'EDITOR' representation. Otherwise, it falls back
 * to the plain text guideline with 'PLAIN' representation.
 *
 * @param classification - The classification level containing guideline fields
 * @param isADFEnabled - Whether the ADF feature flag is enabled (from fg('confluence_dlp_guideline_rich_text_support'))
 * @returns Object containing:
 *   - guideline: The guideline content (ADF string or plain text, or undefined if neither exists)
 *   - guidelineRepresentation: 'EDITOR' for ADF content, 'PLAIN' for plain text
 *
 * @example
 * // With feature flag ON and guidelineADF present
 * const isADFEnabled = fg('confluence_dlp_guideline_rich_text_support');
 * const { guideline, guidelineRepresentation } = getGuidelineProps(classification, isADFEnabled);
 * // Returns: { guideline: '{"type":"doc"...}', guidelineRepresentation: 'EDITOR' }
 *
 * @example
 * // With feature flag OFF or no guidelineADF
 * const isADFEnabled = fg('confluence_dlp_guideline_rich_text_support');
 * const { guideline, guidelineRepresentation } = getGuidelineProps(classification, isADFEnabled);
 * // Returns: { guideline: 'Plain text guideline', guidelineRepresentation: 'PLAIN' }
 */
export const getGuidelineProps = (
	classification: ClassificationLevel | null | undefined,
	isADFEnabled: boolean,
): GuidelineProps => {
	// When feature flag is enabled, prefer ADF guideline over plain text
	if (isADFEnabled && classification?.guidelineADF) {
		return {
			guideline: classification.guidelineADF,
			guidelineRepresentation: 'EDITOR',
		};
	}

	// Fall back to plain text guideline
	return {
		guideline: classification?.guideline,
		guidelineRepresentation: 'PLAIN',
	};
};
